import { createTheme, ThemeOptions } from '@mui/material/styles';
import type { Shadows } from '@mui/material/styles';
import { createGlobalStyles } from './globalStyles';
import { colors, colorsDark, typography, shadows, shadowsDark, borders, spacing, motion, zIndex } from './tokens';

// Extend MUI Theme type
declare module '@mui/material/styles' {
  interface Theme {
    custom: {
      colors: typeof colors;
      typography: typeof typography;
      shadows: typeof shadows;
      borders: typeof borders;
      spacing: typeof spacing;
      motion: typeof motion;
      zIndex: typeof zIndex;
    };
  }

  interface ThemeOptions {
    custom?: {
      colors?: typeof colors;
      typography?: typeof typography;
      shadows?: typeof shadows;
      borders?: typeof borders;
      spacing?: typeof spacing;
      motion?: typeof motion;
      zIndex?: typeof zIndex;
    };
  }
}

/**
 * Create a Bennie MUI theme for the given mode.
 *
 * SURFACES: light mode uses gradients; dark mode is flat, because §5.1 suppresses shadows on dark
 * and a dark surface is distinguished by its background colour instead.
 * ⚠️ THE BRAND GRADIENT IS NOT PART OF THAT RULE and never was — §2.5 gives it to the "header bar,
 * primary buttons, active tab pills" with no mode qualifier, and §2.6 resolves its two token slots
 * per mode. The former wording here ("dark mode uses flat colors per §5.1") was read as covering
 * the primary button, which is how that button ended up flat on the registry and gradient on the
 * PROD-inherited screens. §5.1 is a SHADOW table; it says nothing about a button's fill.
 */
export function createAppTheme(mode: 'light' | 'dark') {
  const isDark = mode === 'dark';
  const c = isDark ? colorsDark : colors;
  const s = isDark ? shadowsDark : shadows;

  const themeOptions: ThemeOptions = {
    palette: {
      mode,
      action: { disabled: c.disabled.ink, disabledBackground: c.disabled.fill },
      primary: {
        main: c.primary.main,
        dark: c.primary.dark,
        light: c.primary.light,
        contrastText: c.primary.contrastText,
      },
      secondary: {
        main: c.accent.gold,
        dark: c.accent.goldDark,
        light: c.accent.gold,
        contrastText: isDark ? c.text.inverse : colors.primary[800],
      },
      success: {
        main: c.success.main,
        dark: c.success.dark,
        light: c.success.light,
        contrastText: c.success.contrastText,
      },
      warning: {
        main: c.warning.main,
        dark: c.warning.dark,
        light: c.warning.light,
        contrastText: c.warning.contrastText,
      },
      error: {
        main: c.error.main,
        dark: c.error.dark,
        light: c.error.light,
        contrastText: c.error.contrastText,
      },
      background: {
        default: c.background.default,
        paper: c.background.paper,
      },
      text: {
        primary: c.text.primary,
        secondary: c.text.secondary,
        disabled: c.text.disabled,
      },
      grey: {
        50: c.neutral[50],
        100: c.neutral[100],
        200: c.neutral[200],
        300: c.neutral[300],
        400: c.neutral[400],
        500: c.neutral[500],
        600: c.neutral[600],
        700: c.neutral[700],
        800: c.neutral[800],
        900: c.neutral[900],
      },
      divider: c.border.light,
    },

    typography: {
      fontFamily: typography.fontFamily.primary,
      h1: {
        fontSize: typography.typeRoles.pageTitle.size,
        fontWeight: typography.typeRoles.pageTitle.weight,
        lineHeight: typography.typeRoles.pageTitle.lineHeight,
        letterSpacing: typography.typeRoles.pageTitle.letterSpacing,
        color: c.text.primary,
      },
      h2: {
        fontSize: typography.typeRoles.section.size,
        fontWeight: typography.typeRoles.section.weight,
        lineHeight: typography.typeRoles.section.lineHeight,
        letterSpacing: typography.typeRoles.section.letterSpacing,
        color: c.text.primary,
      },
      h3: {
        fontSize: typography.typeRoles.cardTitle.size,
        fontWeight: typography.typeRoles.cardTitle.weight,
        lineHeight: typography.typeRoles.cardTitle.lineHeight,
        letterSpacing: typography.typeRoles.cardTitle.letterSpacing,
        color: c.text.primary,
      },
      h4: {
        fontSize: typography.fontSize.lg,
        fontWeight: typography.fontWeight.semibold,
        lineHeight: typography.lineHeight.normal,
        color: c.text.primary,
      },
      h5: {
        fontSize: typography.fontSize.md,
        fontWeight: typography.fontWeight.semibold,
        lineHeight: typography.lineHeight.normal,
        color: c.text.primary,
      },
      h6: {
        fontSize: typography.fontSize.base,
        fontWeight: typography.fontWeight.semibold,
        lineHeight: typography.lineHeight.default,
        color: c.text.primary,
      },
      body1: {
        fontSize: typography.typeRoles.body.size,
        fontWeight: typography.typeRoles.body.weight,
        lineHeight: typography.typeRoles.body.lineHeight,
        letterSpacing: typography.typeRoles.body.letterSpacing,
        color: c.text.secondary,
      },
      body2: {
        fontSize: typography.typeRoles.bodySm.size,
        fontWeight: typography.typeRoles.bodySm.weight,
        lineHeight: typography.typeRoles.bodySm.lineHeight,
        letterSpacing: typography.typeRoles.bodySm.letterSpacing,
        color: c.text.secondary,
      },
      caption: {
        fontSize: typography.typeRoles.caption.size,
        fontWeight: typography.typeRoles.caption.weight,
        lineHeight: typography.typeRoles.caption.lineHeight,
        letterSpacing: typography.typeRoles.caption.letterSpacing,
        color: c.text.tertiary,
      },
      overline: {
        fontSize: typography.typeRoles.overline.size,
        fontWeight: typography.typeRoles.overline.weight,
        lineHeight: typography.typeRoles.overline.lineHeight,
        letterSpacing: typography.typeRoles.overline.letterSpacing,
        textTransform: 'uppercase',
      },
      button: {
        fontSize: typography.typeRoles.bodySm.size,
        fontWeight: typography.fontWeight.semibold,
        textTransform: 'none',
        letterSpacing: '0.3px',
      },
      subtitle1: {
        fontSize: typography.fontSize.base,
        fontWeight: typography.fontWeight.semibold,
        color: c.text.primary,
      },
      subtitle2: {
        fontSize: typography.fontSize.sm,
        fontWeight: typography.fontWeight.semibold,
        color: c.text.primary,
      },
    },

    shape: {
      borderRadius: parseInt(borders.radius.md),
    },

    shadows: [
      'none',
      s.sm,
      s.md,
      s.lg,
      s.xl,
      // MUI default shadows 5-24 (keeping defaults for compatibility)
      '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
      '0px 3px 3px -2px rgba(0,0,0,0.2), 0px 3px 4px 0px rgba(0,0,0,0.14), 0px 1px 8px 0px rgba(0,0,0,0.12)',
      '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
      '0px 3px 5px -1px rgba(0,0,0,0.2), 0px 5px 8px 0px rgba(0,0,0,0.14), 0px 1px 14px 0px rgba(0,0,0,0.12)',
      '0px 3px 5px -1px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0,0,0,0.14), 0px 1px 18px 0px rgba(0,0,0,0.12)',
      '0px 4px 5px -2px rgba(0,0,0,0.2), 0px 7px 10px 1px rgba(0,0,0,0.14), 0px 2px 16px 1px rgba(0,0,0,0.12)',
      '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
      '0px 5px 6px -3px rgba(0,0,0,0.2), 0px 9px 12px 1px rgba(0,0,0,0.14), 0px 3px 16px 2px rgba(0,0,0,0.12)',
      '0px 6px 6px -3px rgba(0,0,0,0.2), 0px 10px 14px 1px rgba(0,0,0,0.14), 0px 4px 18px 3px rgba(0,0,0,0.12)',
      '0px 6px 7px -4px rgba(0,0,0,0.2), 0px 11px 15px 1px rgba(0,0,0,0.14), 0px 4px 20px 3px rgba(0,0,0,0.12)',
      '0px 7px 8px -4px rgba(0,0,0,0.2), 0px 12px 17px 2px rgba(0,0,0,0.14), 0px 5px 22px 4px rgba(0,0,0,0.12)',
      '0px 7px 8px -4px rgba(0,0,0,0.2), 0px 13px 19px 2px rgba(0,0,0,0.14), 0px 5px 24px 4px rgba(0,0,0,0.12)',
      '0px 7px 9px -4px rgba(0,0,0,0.2), 0px 14px 21px 2px rgba(0,0,0,0.14), 0px 5px 26px 4px rgba(0,0,0,0.12)',
      '0px 8px 9px -5px rgba(0,0,0,0.2), 0px 15px 22px 2px rgba(0,0,0,0.14), 0px 6px 28px 5px rgba(0,0,0,0.12)',
      '0px 8px 10px -5px rgba(0,0,0,0.2), 0px 16px 24px 2px rgba(0,0,0,0.14), 0px 6px 30px 5px rgba(0,0,0,0.12)',
      '0px 8px 11px -5px rgba(0,0,0,0.2), 0px 17px 26px 2px rgba(0,0,0,0.14), 0px 6px 32px 5px rgba(0,0,0,0.12)',
      '0px 9px 11px -5px rgba(0,0,0,0.2), 0px 18px 28px 2px rgba(0,0,0,0.14), 0px 7px 34px 6px rgba(0,0,0,0.12)',
      '0px 9px 12px -6px rgba(0,0,0,0.2), 0px 19px 29px 2px rgba(0,0,0,0.14), 0px 7px 36px 6px rgba(0,0,0,0.12)',
      '0px 10px 13px -6px rgba(0,0,0,0.2), 0px 20px 31px 3px rgba(0,0,0,0.14), 0px 8px 38px 7px rgba(0,0,0,0.12)',
      '0px 10px 13px -6px rgba(0,0,0,0.2), 0px 21px 33px 3px rgba(0,0,0,0.14), 0px 8px 40px 7px rgba(0,0,0,0.12)',
    ] as Shadows,

    // Custom properties — pass mode-appropriate tokens
    custom: {
      colors: c as typeof colors,
      typography,
      shadows: s as typeof shadows,
      borders,
      spacing,
      motion,
      zIndex,
    },

    // Component overrides
    components: {
      MuiCssBaseline: {
        styleOverrides: () => createGlobalStyles(isDark),
      },

      // Paper component
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: borders.radius.lg,
            boxShadow: s.surface.base,
            border: `1px solid ${c.border.light}`,
            backgroundImage: isDark
              ? 'none'
              : `linear-gradient(135deg, ${c.background.paper} 0%, ${c.accent.surfaceHi} 100%)`,
            backgroundColor: isDark ? c.background.paper : undefined,
          },
        },
      },

      // Button component
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: borders.radius.md,
            textTransform: 'none',
            fontWeight: typography.fontWeight.semibold,
            padding: '10px 20px',
            letterSpacing: '0.3px',
            transition: `transform ${motion.duration.normal} ${motion.easing.default}, box-shadow ${motion.duration.normal} ${motion.easing.default}`,
            boxShadow: 'none',
            '&.Mui-disabled': {
              // The palette's own disabled ink + fill are the dimming (`colors.disabled`). Opacity on top of them
              // composited a pale label onto a pale fill (1.6:1 dark, 1.9:1 light).
              opacity: 1,
              cursor: 'not-allowed',
              pointerEvents: 'auto',
              boxShadow: 'none',
              transform: 'none',
            },
            '&:hover': {
              transform: 'translateY(-2px)',
            },
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          },
          // 🔴 THE BRAND GRADIENT IS NOT MODE-CONDITIONAL. Style guide §6.1 gives Primary a
          // "Brand gradient (`--primary` → `--primary-600`)" with no light-mode qualifier, and §2.5
          // lists "primary buttons" among the brand gradient's consumers. §2.6 ("every token
          // resolves to its dark value") is what makes ONE expression correct in both modes: the
          // dark ramp inverts, so `primary.main → primary[600]` reads #0067b8 → #004080 on light
          // and #3399ff → #66b3ff on dark. That dark pair is what the PROD-inherited buttons
          // already paint from their own `sx`; this branch used to flatten it to a solid fill, so
          // the same button rendered gradient on one screen and flat on another.
          // ⚠️ §5.1's "dark mode is flat" is about SHADOWS and surfaces, not about this button.
          // Dark: `primary.main` text on a card or a primary-tinted surface is 3.95–4.4:1; the light step clears 4.5.
          textPrimary: isDark ? { color: c.primary.light } : {},
          outlinedPrimary: isDark ? { color: c.primary.light } : {},
          // `outlined` pins its label to text.secondary in dark, which hid `color="error"`; the 800 step keeps
          // the red reading and clears 4.5:1 on the error-tinted hover fill.
          outlinedError: isDark ? { color: c.error[800] } : {},
          textError: isDark ? { color: c.error[800] } : {},
          containedPrimary: {
            backgroundImage: `linear-gradient(135deg, ${c.primary.main} 0%, ${c.primary[600]} 100%)`,
            boxShadow: s.primary.default,
            // Legibility: the brand gradient would otherwise paint over the disabled fill.
            '&.Mui-disabled': { backgroundImage: 'none' },
            '&:hover': {
              backgroundImage: `linear-gradient(135deg, ${c.primary[600]} 0%, ${c.primary[700]} 100%)`,
              boxShadow: s.primary.hover,
            },
          },
          containedSecondary: {
            backgroundImage: isDark
              ? 'none'
              : `linear-gradient(135deg, ${c.accent.gold} 0%, ${c.accent.goldDark} 100%)`,
            '&.Mui-disabled': { backgroundImage: 'none' },
            backgroundColor: isDark ? c.accent.gold : undefined,
            color: isDark ? c.text.inverse : colors.primary[800],
            boxShadow: s.gold.default,
            '&:hover': {
              backgroundImage: isDark
                ? 'none'
                : `linear-gradient(135deg, ${c.accent.goldDark} 0%, ${c.accent.goldDark} 100%)`,
              backgroundColor: isDark ? c.accent.goldDark : undefined,
              boxShadow: s.gold.hover,
            },
          },
          containedSuccess: {
            '&.Mui-disabled': { backgroundImage: 'none' },
            backgroundImage: isDark
              ? 'none'
              : `linear-gradient(135deg, ${c.success.main} 0%, rgba(16, 124, 16, 0.85) 100%)`,
            backgroundColor: isDark ? c.success.main : undefined,
            boxShadow: s.success.default,
            '&:hover': {
              boxShadow: s.success.hover,
            },
          },
          outlined: {
            backgroundImage: isDark
              ? 'none'
              : `linear-gradient(135deg, ${c.background.paper} 0%, ${c.accent.surfaceHi} 100%)`,
            backgroundColor: isDark ? c.background.paper : undefined,
            borderWidth: '1.5px',
            borderColor: c.neutral[300],
            color: isDark ? c.text.secondary : c.neutral[700],
            boxShadow: s.xs,
            '&:hover': {
              borderColor: c.primary[100],
              backgroundImage: isDark
                ? 'none'
                : `linear-gradient(135deg, ${c.neutral[50]} 0%, ${c.background.paper} 100%)`,
              // Dark: neutral[200] (#404040) left the secondary-ink label at 4.1:1 while hovered.
              backgroundColor: isDark ? c.accent.surfaceHi : undefined,
              boxShadow: s.primary.default,
            },
          },
        },
      },

      // Tab strip — PILLS, not MUI's default underline indicator.
      // Both authorities agree, so this is not a mock-vs-guide call: the locked mock draws `.tab`
      // as a bordered pill (`padding:8px 24px; border-radius:var(--r-md); font:14px/600;
      // background:var(--paper); border:1px solid var(--border-light); color:var(--text-secondary)`)
      // and `.tab[aria-selected="true"]` as `background-image:var(--grad-brand); border-color:
      // transparent; color:#fff; box-shadow:0 2px 8px rgba(0,103,184,.3)`; style guide §2.5 lists
      // "active tab pills" among the brand gradient's consumers and §2.7 pins the pill's own
      // colours to tokens. It lives in the theme rather than in one screen's `sx` so that every tab
      // strip converges at once — a per-screen pill is how a four-way width divergence started.
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 40,
          },
          flexContainer: {
            gap: `${spacing[2]}px`,
          },
          // The pill IS the selection signal; a second indicator underneath it is noise.
          indicator: {
            display: 'none',
          },
        },
      },
      // The step number is drawn in `primary.contrastText`, which is dark ink in dark mode; the idle
      // disc must be light enough to carry it (text.disabled #6b6b6b measures 3.3:1 against that ink).
      MuiStepIcon: {
        styleOverrides: {
          root: isDark ? { color: c.text.secondary } : {},
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 40,
            padding: `0 ${spacing[5] + 2}px`,
            borderRadius: borders.radius.md,
            border: `${borders.width.thin} solid ${c.border.light}`,
            backgroundColor: c.background.paper,
            color: c.text.secondary,
            fontSize: typography.typeRoles.body.size,
            fontWeight: typography.fontWeight.semibold,
            textTransform: 'none',
            transition: `background-color ${motion.duration.normal} ${motion.easing.default}, color ${motion.duration.normal} ${motion.easing.default}, border-color ${motion.duration.normal} ${motion.easing.default}, transform ${motion.duration.instant} ${motion.easing.default}`,
            '&:hover': {
              backgroundColor: c.primary.wash,
              borderColor: c.primary.light,
              color: c.primary.main,
            },
            '&:active': {
              transform: 'scale(0.97)',
            },
            '&.Mui-selected': {
              backgroundImage: `linear-gradient(135deg, ${c.primary.main} 0%, ${c.primary[600]} 100%)`,
              borderColor: 'transparent',
              color: c.primary.contrastText,
              boxShadow: s.primary.default,
            },
          },
        },
      },

      // TextField component
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: borders.radius.lg, // Style guide §6.2: inputs use lg (12px)
              backgroundImage: isDark
                ? 'none'
                : `linear-gradient(135deg, ${c.neutral[50]} 0%, ${c.accent.surfaceTint} 100%)`,
              // Dark: #404040 under a half-opacity white placeholder measured 3.9:1. The #333 field with a
              // solid secondary placeholder is 5.0:1; the 1.5px border keeps the field edge visible.
              backgroundColor: isDark ? c.accent.surfaceHi : undefined,
              '&:focus-within': {
                boxShadow: 'none',
              },
              '& fieldset': {
                borderColor: c.border.control,
                borderWidth: '1.5px',
              },
              // Placeholders carry meaning (what to type): the token, solid, in both themes.
              '& input::placeholder, & textarea::placeholder': { color: c.text.placeholder, opacity: 1 },
              '&:hover fieldset': {
                borderColor: c.primary.main,
              },
              '&.Mui-focused fieldset': {
                borderColor: c.primary[400],
                borderWidth: '2px',
              },
              '&.Mui-focused': {
                backgroundImage: isDark
                  ? 'none'
                  : `linear-gradient(135deg, ${c.background.paper} 0%, ${c.accent.surfaceHi} 100%)`,
                backgroundColor: isDark ? c.background.paper : undefined,
                boxShadow: `0 0 0 3px ${c.primaryOverlay.medium}`,
              },
              '&.Mui-disabled': {
                backgroundColor: c.background.secondary,
                opacity: 0.5,
                '& fieldset': {
                  borderColor: c.border.medium,
                },
              },
            },
            '& .MuiInputBase-input:focus-visible': {
              outline: 'none',
              boxShadow: 'none',
            },
          },
        },
      },

      // OutlinedInput (for standalone usage)
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '&:focus-within': {
              boxShadow: 'none',
            },
            '&.Mui-focused': {
              boxShadow: 'none',
            },
          },
          input: {
            '&:focus-visible': {
              outline: 'none',
              boxShadow: 'none',
            },
          },
        },
      },

      // Chip component
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: borders.radius.xs,
            fontWeight: typography.fontWeight.semibold,
            letterSpacing: '0.3px',
          },
          sizeSmall: {
            height: '20px',
            fontSize: typography.typeRoles.badge.size,
          },
        },
      },

      // Card component
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: borders.radius.lg,
            boxShadow: s.surface.base,
            border: `1px solid ${c.border.light}`,
            backgroundImage: isDark
              ? 'none'
              : `linear-gradient(135deg, ${c.background.paper} 0%, ${c.accent.surfaceHi} 100%)`,
            backgroundColor: isDark ? c.background.paper : undefined,
            transition: `transform ${motion.duration.normal} ${motion.easing.default}, box-shadow ${motion.duration.normal} ${motion.easing.default}, border-color ${motion.duration.instant} ${motion.easing.default}`,
            '&:hover': {
              boxShadow: s.surface.hover,
              transform: 'translateY(-2px)',
              borderColor: c.primary[100],
            },
          },
        },
      },

      // Dialog component — consistent defaults
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: borders.radius.lg,
            boxShadow: s.lg,
            backgroundImage: 'none',
            backgroundColor: isDark ? c.background.secondary : c.background.paper,
          },
        },
      },

      // Accordion component
      MuiAccordion: {
        styleOverrides: {
          root: {
            borderRadius: borders.radius.lg,
            boxShadow: 'none',
            border: `1px solid ${c.border.light}`,
            backgroundImage: 'none',
            backgroundColor: isDark ? c.background.paper : undefined,
            '&:before': {
              display: 'none',
            },
            '&.Mui-expanded': {
              borderColor: c.primary.main,
            },
          },
        },
      },

      // LinearProgress component
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: borders.radius.sm,
            backgroundColor: c.neutral[200],
          },
          bar: {
            borderRadius: borders.radius.sm,
          },
        },
      },

      // CircularProgress component
      MuiCircularProgress: {
        styleOverrides: {
          root: {
            color: c.primary.main,
          },
        },
      },

      // Alert component — toast/notification surfaces.
      // Alert extends Paper, so the global MuiPaper.root override above paints a
      // surface gradient (white→#fafbfd light, none dark) on top of every Alert.
      // For filled variants that gradient layers over the solid bg color and
      // makes white-contrast text disappear on light mode (border survives because
      // it's a separate property). Clear the inherited backgroundImage and pin
      // the bg + contrastText to palette tokens so dark mode auto-resolves.
      // Toast-grade elevation per style guide §5.1 (--shadow-xl).
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: borders.radius.md,
          },
          filledError: {
            backgroundImage: 'none',
            backgroundColor: c.error.main,
            color: c.error.contrastText,
            border: `1px solid ${c.error.dark}`,
            boxShadow: s.xl,
            '& .MuiAlert-icon': { color: c.error.contrastText, opacity: 1 },
            '& .MuiAlert-message': { color: c.error.contrastText },
            '& .MuiAlert-action button': { color: c.error.contrastText },
          },
          filledWarning: {
            backgroundImage: 'none',
            backgroundColor: c.warning.main,
            color: c.warning.contrastText,
            border: `1px solid ${c.warning.dark}`,
            boxShadow: s.xl,
            '& .MuiAlert-icon': { color: c.warning.contrastText, opacity: 1 },
            '& .MuiAlert-message': { color: c.warning.contrastText },
            '& .MuiAlert-action button': { color: c.warning.contrastText },
          },
          filledSuccess: {
            backgroundImage: 'none',
            backgroundColor: c.success.main,
            color: c.success.contrastText,
            border: `1px solid ${c.success.dark}`,
            boxShadow: s.xl,
            '& .MuiAlert-icon': { color: c.success.contrastText, opacity: 1 },
            '& .MuiAlert-message': { color: c.success.contrastText },
            '& .MuiAlert-action button': { color: c.success.contrastText },
          },
          filledInfo: {
            backgroundImage: 'none',
            backgroundColor: c.primary.main,
            color: c.primary.contrastText,
            border: `1px solid ${c.primary.dark}`,
            boxShadow: s.xl,
            '& .MuiAlert-icon': { color: c.primary.contrastText, opacity: 1 },
            '& .MuiAlert-message': { color: c.primary.contrastText },
            '& .MuiAlert-action button': { color: c.primary.contrastText },
          },
          // Standard (light-tint) variants also need the gradient cleared so the
          // semantic .light tint reads as a flat fill, not gradient-over-fill.
          standardError: {
            backgroundImage: 'none',
            backgroundColor: c.error.light,
            color: isDark ? c.error[800] : c.error.dark,
          },
          standardWarning: {
            backgroundImage: 'none',
            backgroundColor: c.warning[50],
            color: isDark ? c.warning[700] : c.warning.dark,
          },
          standardSuccess: {
            backgroundImage: 'none',
            backgroundColor: c.success.light,
            color: isDark ? c.success[800] : c.success.dark,
          },
          standardInfo: {
            backgroundImage: 'none',
            backgroundColor: c.primary[50],
            color: isDark ? c.primary.light : c.primary.dark,
          },
          // Outlined variants — used pervasively in the toolbox surface. Default MUI
          // outlined Alert is transparent-bg + semantic-color border/text, which on
          // dark mode reads as a faint outline only. Pin a tinted bg + brighter
          // border/text so the banner remains legible in both modes.
          outlinedError: {
            backgroundImage: 'none',
            backgroundColor: c.error.light,
            borderColor: c.error.main,
            color: c.error.main,
            '& .MuiAlert-icon': { color: c.error.main, opacity: 1 },
            '& .MuiAlert-message': { color: isDark ? c.error[800] : c.error.dark },
            '& .MuiAlert-action button': { color: isDark ? c.error[800] : c.error.dark },
          },
          outlinedWarning: {
            backgroundImage: 'none',
            backgroundColor: c.warning[50],
            borderColor: c.warning.main,
            color: c.warning.main,
            '& .MuiAlert-icon': { color: c.warning.main, opacity: 1 },
            '& .MuiAlert-message': { color: isDark ? c.warning.main : c.warning.dark },
            '& .MuiAlert-action button': { color: isDark ? c.warning.main : c.warning.dark },
          },
          outlinedSuccess: {
            backgroundImage: 'none',
            backgroundColor: c.success.light,
            borderColor: c.success.main,
            color: c.success.main,
            '& .MuiAlert-icon': { color: c.success.main, opacity: 1 },
            '& .MuiAlert-message': { color: isDark ? c.success[800] : c.success.dark },
            '& .MuiAlert-action button': { color: isDark ? c.success[800] : c.success.dark },
          },
          outlinedInfo: {
            backgroundImage: 'none',
            backgroundColor: c.primary[50],
            borderColor: c.primary.main,
            color: c.primary.main,
            '& .MuiAlert-icon': { color: c.primary.main, opacity: 1 },
            '& .MuiAlert-message': { color: isDark ? c.primary.light : c.primary.dark },
            '& .MuiAlert-action button': { color: isDark ? c.primary.light : c.primary.dark },
          },
        },
      },
    },
  };

  return createTheme(themeOptions);
}

// Backward-compatible default export (light mode)
export const theme = createAppTheme('light');

export default theme;
